import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Youtube, ArrowRight, Sparkles, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { VIDEOS, CATEGORIES, CYBER_GUY_CHANNEL_URL } from '../data/videos';
import YouTubeVideoCard from '../components/YouTubeVideoCard';
import SEO, { SITE, breadcrumb, orgRef } from '../components/SEO';

const CyberGuyPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [category, setCategory] = useState('All Videos');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    let list = category === 'All Videos' ? VIDEOS : VIDEOS.filter((v) => v.category === category);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((v) =>
        v.title.toLowerCase().includes(q) ||
        (v.description || '').toLowerCase().includes(q) ||
        (v.service || '').toLowerCase().includes(q) ||
        (v.category || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [category, query]);

  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      <SEO
        path="/the-cyber-guy"
        title="The Cyber Guy | Simple. Practical. Actionable Cybersecurity."
        description="The Cyber Guy is Kenny Vic's public cybersecurity education brand. Practical, real-world explanations of cybersecurity for business owners and everyday technology users."
        jsonLd={[
          breadcrumb([
            { name: 'Home', path: '/' },
            { name: 'The Cyber Guy', path: '/the-cyber-guy' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'The Cyber Guy — Cybersecurity Video Library',
            url: `${SITE}/the-cyber-guy`,
            publisher: orgRef,
            hasPart: VIDEOS.map((v) => ({
              '@type': 'VideoObject',
              name: v.title,
              description: v.description,
              thumbnailUrl: v.thumbnailUrl,
              contentUrl: v.youtubeUrl,
              embedUrl: v.embedUrl,
              publisher: orgRef,
              url: `${SITE}/the-cyber-guy/${v.slug}`,
            })),
          },
        ]}
      />
      {/* HERO — dark navy (media identity) */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(22,135,255,0.16),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-14 sm:pb-20">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-400/30 rounded-full text-xs sm:text-sm text-cyan-300 mb-6 font-medium">
            <Sparkles className="w-3.5 h-3.5" /> Public Cybersecurity Education
          </span>
          <h1 className="font-editorial text-4xl sm:text-6xl lg:text-[4.25rem] font-semibold leading-[1.05] tracking-tight text-white">
            The Cyber Guy
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-cyan-100/90 font-medium">
            Simple. Practical. Actionable Cybersecurity.
          </p>
          <p className="mt-5 text-base sm:text-lg text-slate-300/95 leading-relaxed max-w-3xl">
            Cybersecurity doesn&apos;t have to be complicated. Through The Cyber Guy, Kenny Vic explains cybersecurity using simple, practical and real-world examples that business owners, employees and everyday technology users can understand and act on.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href={CYBER_GUY_CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button
                data-testid="cg-watch-on-youtube"
                className="w-full sm:w-auto px-6 py-5 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold"
              >
                <Youtube className="w-4 h-4 mr-2" /> Watch on YouTube
              </Button>
            </a>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                variant="outline"
                data-testid="cg-work-with-us"
                className="w-full sm:w-auto px-6 py-5 bg-transparent border-cyan-300/50 text-cyan-100 hover:bg-white/5 hover:text-white rounded-full"
              >
                Work with Kennyvicops <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SEARCH + FILTERS */}
      <section className="px-4 sm:px-6 py-8 border-b border-slate-900 sticky top-16 sm:top-20 z-20 bg-slate-950/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search videos by title, topic or service…"
              data-testid="cg-search"
              className="pl-10 h-11 bg-slate-900/70 border-cyan-500/25 text-white placeholder:text-slate-500 focus:border-cyan-400"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`cg-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`px-3.5 py-1.5 text-xs sm:text-sm rounded-full border transition-colors ${
                  category === c
                    ? 'bg-cyan-500/15 border-cyan-400/50 text-cyan-200'
                    : 'bg-slate-900/60 border-slate-800 text-slate-300 hover:border-cyan-400/40'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO GRID */}
      <section className="px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm text-slate-400 mb-6">
            Showing <span className="text-white font-medium">{filtered.length}</span> of{' '}
            <span className="text-white font-medium">{VIDEOS.length}</span> videos
          </p>
          {filtered.length ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((v) => (
                <YouTubeVideoCard key={v.youtubeId} video={v} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-slate-400">
              No videos match your filters. Try clearing the search or picking a different category.
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA — WHITE (Kennyvicops mode; clarifies Cyber Guy → Kennyvicops handoff) */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <span className="w-6 h-px bg-blue-600/70" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">From Education to Action</span>
          </div>
          <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-[#06101F] leading-[1.08]">
            Learn about cybersecurity. Apply it to your business.
          </h2>
          <p className="mt-5 text-[#475467] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            The Cyber Guy helps you understand cybersecurity. Kennyvicops helps you put it into practice.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={CYBER_GUY_CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button
                variant="outline"
                data-testid="cg-final-yt"
                className="w-full sm:w-auto px-7 py-6 bg-white border-slate-300 text-[#101828] hover:bg-slate-50 rounded-full font-medium"
              >
                <Youtube className="w-4 h-4 mr-2" /> Watch More on YouTube
              </Button>
            </a>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                data-testid="cg-final-work-with-us"
                className="w-full sm:w-auto px-7 py-6 bg-[#06101F] hover:bg-[#0B1B35] text-white rounded-full font-semibold"
              >
                Work with Kennyvicops <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CyberGuyPage;
